import React, { useEffect, useMemo, useState } from "react";
import StatisticsSection from "./StatisticsSection";

import { getGroupsHome } from "../../services/groupServices";
import { getAdherentsByGroup } from "../../services/dashboardServices";
import { getAllTeamMembers } from "../../services/teamServices";
import { getSettings } from "../../services/settingsServices";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3317";

const DEFAULT_VISUAL = "/img/headercount_new.webp";
const DEFAULT_KANJI = "/img/judo-kanji.webp";

const resolveVisualUrl = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  // Cloudinary, image distante, data, blob
  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  // IMPORTANT :
  // les images dans public/img doivent rester côté front
  if (cleanUrl.startsWith("/img/")) {
    return cleanUrl;
  }

  // fichiers uploadés backend
  if (cleanUrl.startsWith("/uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  if (cleanUrl.startsWith("uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
  }

  // fallback : chemin public front
  if (cleanUrl.startsWith("/")) {
    return cleanUrl;
  }

  return `/${cleanUrl}`;
};

const isGroupActive = (group) => {
  if (
    group?.is_active === undefined ||
    group?.is_active === null ||
    group?.is_active === ""
  ) {
    return true;
  }

  return (
    group.is_active === true ||
    group.is_active === 1 ||
    group.is_active === "1"
  );
};

const countLicensedFromGroups = (items = []) => {
  if (!Array.isArray(items) || !items.length) return 0;

  return items.reduce((total, item) => {
    return total + Number(item?.adherentCount || 0);
  }, 0);
};

const countTeachers = (members = []) => {
  if (!Array.isArray(members)) return 0;

  return members.filter((member) => {
    const haystack = `${member?.categorie || ""} ${member?.fonction || ""}`
      .toLowerCase()
      .trim();

    return (
      haystack.includes("enseign") ||
      haystack.includes("prof") ||
      haystack.includes("assistant") ||
      haystack.includes("bpjeps") ||
      haystack.includes("coach")
    );
  }).length;
};

export default function HeaderCount() {
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    licencies: 0,
    groupes: 0,
    enseignants: 0,
    inscritsEtCoursEssai: 0,
  });

  const [visuals, setVisuals] = useState({
    visualUrl: DEFAULT_VISUAL,
    kanjiUrl: DEFAULT_KANJI,
  });

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [groupsRes, adherentsRes, teamRes, settingsRes] =
          await Promise.allSettled([
            getGroupsHome(),
            getAdherentsByGroup(),
            getAllTeamMembers(),
            getSettings(),
          ]);

        if (!mounted) return;

        const groupsData =
          groupsRes.status === "fulfilled" && Array.isArray(groupsRes.value)
            ? groupsRes.value
            : [];

        const adherentsByGroupData =
          adherentsRes.status === "fulfilled" &&
          Array.isArray(adherentsRes.value)
            ? adherentsRes.value
            : [];

        const teamData =
          teamRes.status === "fulfilled" && Array.isArray(teamRes.value)
            ? teamRes.value
            : [];

        const settingsArray =
          settingsRes.status === "fulfilled" ? settingsRes.value : [];

        const settings = Array.isArray(settingsArray)
          ? settingsArray[0] || {}
          : settingsArray || {};

        const activeGroups = groupsData.filter(isGroupActive);

        const licencies = countLicensedFromGroups(adherentsByGroupData);
        const groupes = activeGroups.length;
        const enseignants = countTeachers(teamData);

        const inscritsEtCoursEssai = Number(
          settings?.nb_inscrits_essais ||
            settings?.inscrits_essais ||
            settings?.headercount_inscrits_essais ||
            0
        );

        setStats({
          licencies,
          groupes,
          enseignants,
          inscritsEtCoursEssai,
        });

        setVisuals({
          visualUrl: resolveVisualUrl(
            settings?.headercount_image ||
              settings?.stats_image ||
              DEFAULT_VISUAL
          ),
          kanjiUrl: resolveVisualUrl(
            settings?.headercount_kanji ||
              settings?.stats_kanji ||
              DEFAULT_KANJI
          ),
        });
      } catch (error) {
        console.error("Erreur lors du chargement des statistiques :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const sectionTitle = useMemo(() => {
    return "Quelques chiffres";
  }, []);

  return (
    <section id="stats" className="scroll-mt-28">
      <StatisticsSection
        title={sectionTitle}
        loading={loading}
        visualUrl={visuals.visualUrl}
        kanjiUrl={visuals.kanjiUrl}
        licencies={stats.licencies}
        groupes={stats.groupes}
        enseignants={stats.enseignants}
        inscritsEtCoursEssai={stats.inscritsEtCoursEssai}
      />
    </section>
  );
}